  import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import ChartRenderer from './ChartRenderer';
import YouTubeEmbed from './YouTubeEmbed';
import { Project } from '../types';

interface ProjectCardProps {
  project: Project;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project }) => {
  const [expanded, setExpanded] = useState(false);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/services/${project.id}`);
      alert('Project link copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.3 }}
      className="bg-deep-navy/50 backdrop-blur-lg rounded-2xl border border-navy-blue overflow-hidden h-full flex flex-col"
    >
      <div className="p-6 flex-grow">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-2xl font-bold mb-2 text-light">{project.title}</h3>
            <div className="flex items-center gap-4 text-sm text-medium-gray mb-4">
              <span className="text-xl font-bold text-white">From ${project.price_start}<span className="text-sm font-normal">/mo</span></span>
              <span>{project.roi_summary}</span>
            </div>
          </div>
          <button 
            onClick={() => setExpanded(!expanded)}
            className="relative overflow-hidden group text-accent-blue hover:text-light focus:outline-none"
          >
            <span className="flex items-center gap-2">
              {expanded ? 'Show Less' : 'View Details'}
              <motion.span
                initial={false}
                animate={{ rotate: expanded ? 180 : 0 }}
                transition={{ duration: 0.3 }}
                className="inline-block"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 9L12 16L5 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </motion.span>
            </span>
          </button>
        </div>

        <p className="text-light mb-4">{project.short_description}</p>

        <div className="aspect-video bg-dark-navy rounded-lg overflow-hidden mb-4">
          {project.youtube_url ? (
            <YouTubeEmbed 
              videoId={project.youtube_url} 
              title={project.title} 
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-medium-gray">
              Video Preview
            </div>
          )}
        </div>

        {expanded && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="pt-4 mt-4 border-t border-navy-blue flex-grow"
          >
            <p className="text-light mb-4">{project.full_description}</p>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-navy-blue/50 p-3 rounded-lg">
                <h4 className="text-medium-gray text-sm mb-1">Time to Implement</h4>
                <p className="font-medium text-light text-lg">{project.time_to_implement}</p>
              </div>
            </div>
            
            {/* Charts Section */}
            {project.metrics && project.metrics.length > 0 && (
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-xl font-semibold text-light">Project Metrics</h4>
                  <Link 
                    to={`/services/${project.id}`} 
                    className="text-sm text-accent-blue hover:text-blue-300"
                  >
                    View Full Detail
                  </Link>
                </div>
                
                <div className="space-y-4">
                  {/* Show first two metrics */}
                  {project.metrics.slice(0, 2).map((metric) => (
                    <div key={metric.id} className="bg-dark-navy p-4 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <h5 className="font-medium text-lg text-light">{metric.metric_name}</h5>
                        <div className="flex flex-col items-end gap-1">
                          <div className="flex gap-2 text-xs">
                            <span className="bg-navy-blue/30 px-2 py-1 rounded">X: {metric.x_label}</span>
                            <span className="bg-navy-blue/30 px-2 py-1 rounded">Y: {metric.y_label}</span>
                          </div>
                          <Link 
                            to={`/metric/${metric.id}`}
                            className="text-xs text-accent-blue hover:text-blue-300"
                          >
                            View Metric
                          </Link>
                        </div>
                      </div>
                      <ChartRenderer metric={metric} />
                    </div>
                  ))}
                </div>
                
                {/* Show info if more than 2 metrics exist */}
                {project.metrics.length > 2 && (
                  <p className="text-sm text-medium-gray mt-2">
                    + {project.metrics.length - 2} more metric{project.metrics.length - 2 > 1 ? 's' : ''} available in full view
                  </p>
                )}
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Link 
                to={`/services/${project.id}`}
                className="flex-1 bg-gradient-to-r from-navy-blue to-accent-blue hover:from-accent-blue hover:to-navy-blue text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 text-center"
              >
                <span className="flex items-center justify-center gap-2">
                  View Full Details
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 6H6C4.89543 6 4 6.89543 4 8V18C4 19.1046 4.89543 20 6 20H16C17.1046 20 18 19.1046 18 18V14M14 4H18C19.1046 4 20 4.89543 20 6V10M20 4L10 14M20 4V8M4 20L8 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              </Link>
              
              <button 
                onClick={() => window.open('https://calendly.com/waceem/initial-project-consultation', '_blank')}
                className="flex-1 bg-gradient-to-r from-navy-blue/50 to-accent-blue/50 hover:from-accent-blue/50 hover:to-navy-blue/50 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300"
              >
                <span className="flex items-center justify-center gap-2">
                  Schedule Free Demo
                  <motion.span
                    animate={{ x: [0, 5, 0] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </motion.span>
                </span>
              </button>
              
              <button 
                onClick={copyToClipboard}
                className="bg-navy-blue hover:bg-accent-blue text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300"
                title="Copy project link"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 17.9296H6C4.89543 17.9296 4 17.0342 4 15.9296V5.92957C4 4.825 4.89543 3.92957 6 3.92957H14C15.1046 3.92957 16 4.825 16 5.92957V7.92957M10 7.92957H18C19.1046 7.92957 20 8.825 20 9.92957V19.9296C20 21.0342 19.1046 21.9296 18 21.9296H10C8.89543 21.9296 8 21.0342 8 19.9296V7.92957Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default ProjectCard;